import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Volume2, Headphones, Quote } from "lucide-react";
import { fadeUp, staggerContainer, staggerItem } from "@/lib/motion";

const audioTestimonials = [
    {
        id: "ayushi",
        name: "Ayushi",
        tag: "AHC RO/ARO · Rapid Steno User",
        location: "Allahabad High Court Aspirant",
        duration: "1:15",
        avatar: "https://images.pexels.com/photos/7580837/pexels-photo-7580837.jpeg?auto=compress&cs=tinysrgb&w=200",
        excerpt:
            "Maine formatting typing ke liye bahut jagah search kiya — YouTube, Google — but kahin nahi mila. Rapid Steno join karne ke baad meri formatting bahut improve hui. Iska fitment exercise feature sabse best hai.",
        accent: "rgba(0,229,255,0.45)",
        src: "https://customer-assets.emergentagent.com/job_atvicon-futuristic/artifacts/134mvrzc_vidssave.com%20Allahabad%20High%20Court%20%20_%20Rapid%20Steno%20User%20Review%20_%20APS%20PS%20_%20%20RO%20ARO%20_%20AHC%20%23allahabad_highcourt%2048KBPS.mp3",
    },
    {
        id: "riaz",
        name: "Riaz",
        tag: "AHC APS/PS · Rapid Steno User",
        location: "Allahabad High Court Aspirant",
        duration: "0:45",
        avatar: "https://images.pexels.com/photos/5728023/pexels-photo-5728023.jpeg?auto=compress&cs=tinysrgb&w=200",
        excerpt:
            "Pehle meri accuracy aur speed bahut kam thi. Rapid Steno ke Formatting Typing aur Pitman Exercise se accuracy aur speed dono mein bahut improvement aayi. Mistake Analysis feature ne confidence boost kar diya.",
        accent: "rgba(124,92,255,0.45)",
        src: "https://customer-assets.emergentagent.com/job_atvicon-futuristic/artifacts/a14b5y7d_vidssave.com%20Allahabad%20High%20Court%20%20_%20Rapid%20Steno%20User%20Review%20_%20APS%20PS%20_%20%20RO%20ARO%20_%20AHC%20%23allahabad_highcourt%2048KBPS%20%281%29.mp3",
    },
];

const fmt = (s) => {
    if (!isFinite(s) || s < 0) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
};

// Pre-generated waveform bar heights (consistent random feel)
const WAVE_BARS = Array.from({ length: 56 }, (_, i) => {
    const a = Math.sin(i * 0.6) * 0.5 + 0.5;
    const b = Math.sin(i * 1.3 + 1.2) * 0.3 + 0.4;
    return Math.max(0.15, Math.min(1, a * 0.7 + b * 0.5));
});

const AudioCard = ({ item, onPlayRequest, isPlaying, setRefHandlers }) => {
    const audioRef = useRef(null);
    const audioCtxRef = useRef(null);
    const gainRef = useRef(null);
    const [time, setTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const progress = duration > 0 ? time / duration : 0;

    // Lazy-init Web Audio for volume boost (×3)
    const ensureBoost = () => {
        const a = audioRef.current;
        if (!a || audioCtxRef.current) return;
        try {
            const AC = window.AudioContext || window.webkitAudioContext;
            if (!AC) return;
            const ctx = new AC();
            const source = ctx.createMediaElementSource(a);
            const gain = ctx.createGain();
            gain.gain.value = 3.0;
            source.connect(gain).connect(ctx.destination);
            audioCtxRef.current = ctx;
            gainRef.current = gain;
        } catch (e) {
            // already wired or unsupported — fallback to native volume
        }
    };

    useEffect(() => {
        const a = audioRef.current;
        if (!a) return;
        a.volume = 1;
        const onTime = () => setTime(a.currentTime);
        const onLoaded = () => setDuration(a.duration || 0);
        const onEnd = () => {
            setTime(0);
            onPlayRequest(null);
        };
        const onPlay = () => {
            ensureBoost();
            const ctx = audioCtxRef.current;
            if (ctx && ctx.state === "suspended") ctx.resume();
        };
        a.addEventListener("timeupdate", onTime);
        a.addEventListener("loadedmetadata", onLoaded);
        a.addEventListener("ended", onEnd);
        a.addEventListener("play", onPlay);
        setRefHandlers(item.id, {
            play: () => a.play(),
            pause: () => a.pause(),
            reset: () => {
                a.pause();
                a.currentTime = 0;
                setTime(0);
            },
        });
        return () => {
            a.removeEventListener("timeupdate", onTime);
            a.removeEventListener("loadedmetadata", onLoaded);
            a.removeEventListener("ended", onEnd);
            a.removeEventListener("play", onPlay);
            try {
                audioCtxRef.current?.close();
            } catch (e) {
                // ignore
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggle = () => onPlayRequest(isPlaying ? null : item.id);

    const seek = (e) => {
        const a = audioRef.current;
        if (!a || !duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const p = (e.clientX - rect.left) / rect.width;
        a.currentTime = p * duration;
        setTime(a.currentTime);
    };

    return (
        <motion.div variants={staggerItem} className="h-full">
            <div
                data-testid={`audio-testimonial-${item.id}`}
                className="relative glass rounded-2xl p-7 md:p-8 h-full flex flex-col overflow-hidden group"
            >
                <div
                    className="absolute -top-32 -right-24 w-[320px] h-[320px] rounded-full blur-[100px] opacity-50 pointer-events-none"
                    style={{ background: item.accent }}
                />

                <audio
                    ref={audioRef}
                    src={item.src}
                    preload="metadata"
                    crossOrigin="anonymous"
                    data-testid={`audio-element-${item.id}`}
                />

                {/* Header */}
                <div className="relative flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <img
                                src={item.avatar}
                                alt={item.name}
                                className="h-12 w-12 rounded-full object-cover border border-white/15"
                            />
                            {isPlaying && (
                                <span className="absolute inset-0 rounded-full border-2 border-cyan-400 animate-ping" />
                            )}
                        </div>
                        <div>
                            <div className="text-white font-semibold">{item.name}</div>
                            <div className="text-xs text-zinc-500 font-mono">
                                {item.tag}
                            </div>
                        </div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-cyan-400/10 border border-cyan-400/20 px-3 py-1 text-[10px] uppercase tracking-[0.25em] font-mono text-cyan-300">
                        <Headphones className="h-3 w-3" />
                        Audio
                    </span>
                </div>

                {/* Quote excerpt */}
                <div className="relative mt-6">
                    <Quote className="h-6 w-6 text-cyan-300/50" strokeWidth={1.5} />
                    <p className="mt-3 text-zinc-300 text-[15px] leading-relaxed">
                        {item.excerpt}
                    </p>
                </div>

                {/* Waveform + controls */}
                <div className="relative mt-7 flex items-center gap-5">
                    <button
                        onClick={toggle}
                        data-testid={`audio-play-${item.id}`}
                        aria-label={isPlaying ? "Pause" : "Play"}
                        className="relative h-14 w-14 shrink-0 rounded-full bg-cyan-400 hover:bg-cyan-300 text-black grid place-items-center transition shadow-[0_0_30px_-8px_rgba(0,229,255,0.7)] group/btn"
                    >
                        {isPlaying ? (
                            <Pause className="h-5 w-5 fill-black" />
                        ) : (
                            <Play className="h-5 w-5 fill-black ml-0.5" />
                        )}
                        {isPlaying && (
                            <span className="absolute inset-0 rounded-full bg-cyan-400 animate-ping opacity-40" />
                        )}
                    </button>

                    <div className="flex-1 min-w-0">
                        {/* Waveform */}
                        <button
                            type="button"
                            onClick={seek}
                            className="relative w-full h-12 flex items-center gap-[3px] cursor-pointer"
                            aria-label="Seek"
                        >
                            {WAVE_BARS.map((h, i) => {
                                const filled = i / WAVE_BARS.length <= progress;
                                return (
                                    <motion.span
                                        key={i}
                                        animate={
                                            isPlaying && filled
                                                ? { scaleY: [1, 1.35, 1] }
                                                : { scaleY: 1 }
                                        }
                                        transition={{
                                            duration: 0.9,
                                            repeat: isPlaying ? Infinity : 0,
                                            delay: (i % 8) * 0.07,
                                            ease: "easeInOut",
                                        }}
                                        className="flex-1 rounded-sm origin-center"
                                        style={{
                                            height: `${h * 100}%`,
                                            background: filled
                                                ? "linear-gradient(180deg, #00e5ff, #7c5cff)"
                                                : "rgba(255,255,255,0.18)",
                                            boxShadow: filled
                                                ? "0 0 8px rgba(0,229,255,0.5)"
                                                : "none",
                                        }}
                                    />
                                );
                            })}
                        </button>

                        {/* Time row */}
                        <div className="mt-2 flex items-center justify-between font-mono text-[11px] text-zinc-500">
                            <span>{fmt(time)}</span>
                            <span className="flex items-center gap-1.5">
                                <Volume2 className="h-3 w-3" />
                                Rapid Steno · Live testimonial
                            </span>
                            <span>{fmt(duration) || item.duration}</span>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

const AudioTestimonials = () => {
    const [playingId, setPlayingId] = useState(null);
    const handlersRef = useRef({});

    const setRefHandlers = (id, handlers) => {
        handlersRef.current[id] = handlers;
    };

    const onPlayRequest = (id) => {
        // Pause all others
        Object.entries(handlersRef.current).forEach(([k, h]) => {
            if (k !== id) h.pause();
        });
        if (id) {
            handlersRef.current[id]?.play();
        }
        setPlayingId(id);
    };

    return (
        <section
            id="audio-testimonials"
            data-testid="audio-testimonials-section"
            className="section py-24 md:py-32 bg-[#070708]/70"
        >
            <motion.div
                className="orb"
                style={{
                    width: 520,
                    height: 520,
                    top: "15%",
                    right: "-200px",
                    background:
                        "radial-gradient(circle, rgba(0,229,255,0.18), transparent 60%)",
                }}
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
            />
            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
                <motion.div {...fadeUp} className="max-w-3xl mb-12">
                    <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-cyan-300">
                        <Headphones className="h-4 w-4" />
                        06 / Audio Reviews
                    </span>
                    <h2
                        data-testid="audio-testimonials-heading"
                        className="mt-4 font-display font-semibold text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
                    >
                        Hear it from
                        <br />
                        <span className="text-grad-cyan">our users.</span>
                    </h2>
                    <p className="mt-5 text-zinc-400 text-lg">
                        Raw, unedited voice reviews from Allahabad High Court aspirants
                        using Rapid Steno every single day.
                    </p>
                </motion.div>

                <motion.div
                    variants={staggerContainer}
                    initial="initial"
                    whileInView="whileInView"
                    viewport={{ once: true, margin: "-80px" }}
                    className="grid md:grid-cols-2 gap-6"
                >
                    {audioTestimonials.map((item) => (
                        <AudioCard
                            key={item.id}
                            item={item}
                            isPlaying={playingId === item.id}
                            onPlayRequest={onPlayRequest}
                            setRefHandlers={setRefHandlers}
                        />
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default AudioTestimonials;
